import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Form,
  FormInstance,
  AutoComplete,
  Select,
  Input,
  Row,
  Col,
  notification,
} from "antd";
import { OptionData, OptionGroupData } from "rc-select/lib/interface";
import CustomButton from "../../../common/Buttons/CustomButton";
import styles from "./PersonForm.module.scss";
import {
  getAutoCompleteAddress,
  getPlaceDetails,
  getWandaServiceArea,
  validateUspsAddress,
} from "../../../services/LocationService";
import {
  Address,
  AddressRef,
  AutoCompleteOption,
  defaultSelectedAddress,
} from "../../../types/LocationTypes";
import InvalidAddressModal from "../Modals/InvalidAddressModal";
import { useServiceUserCreationCtx } from "../../../context/ServiceUserCreationContext";
import {
  constructAddressByPlaceDetails,
  validateAddressObject,
} from "../../../helpers/personHelpers";
import { useLocation } from "react-router-dom";

type Props = {
  addressType: "homeAddress" | "mailingAddress";
  form: FormInstance;
  labels: Record<string, string>;
  validateAndCheckForm: Function;
  shouldValidateZip: boolean;
};

const PersonAddress = forwardRef<AddressRef, Props>((props, ref) => {
  const {
    form,
    labels,
    addressType,
    validateAndCheckForm,
    shouldValidateZip,
  } = props;

  const [isEnterManualAddress, setIsEnterManualAddress] = useState<boolean>(
    false
  );
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<
    AutoCompleteOption[]
  >([]);
  const [selectedAddress, setSelectedAddress] = useState<Address>(
    defaultSelectedAddress
  );
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [
    isInvalidAddressModalVisible,
    setIsInvalidAddressModalVisible,
  ] = useState<boolean>(false);
  const [isUspsAddressError, setIsUspsAddressError] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    handleResetAddressLookup,
    validateManualAddress,
    isEnterManualAddress,
  }));

  const { pathname } = useLocation();

  const { statesCtx, getStateList } = useServiceUserCreationCtx();
  const [states] = statesCtx;

  useEffect(() => {
    !states.length && getStateList();
  }, []);

  useEffect(() => {
    const isInitialAddressAvailable = form.getFieldValue(addressType)?.address1;
    const isSameAsHomeAddress = form.getFieldValue("isSameAsHomeAddress");
    const isHomeAddress = addressType === "homeAddress";
    const isProfile = pathname
      .split("/")
      .some((pathSegment) =>
        ["EditProfile", "EditServiceProfile"].includes(pathSegment)
      );

    if (
      !isProfile &&
      isInitialAddressAvailable &&
      (isHomeAddress || !isSameAsHomeAddress)
    ) {
      (async () => {
        try {
          const { zip } = form.getFieldValue(addressType);
          await checkZipUnderServiceArea(zip);
        } catch (error) {
          notification.error({
            message: "Zip Validation Failed.",
          });
        }
      })();
    }
  }, []);

  async function validateManualAddress() {
    if (!isEnterManualAddress) {
      return { isValid: true, uspsAddress: null };
    }

    try {
      const uspsAddress = await validateUspsAddress(
        form.getFieldValue(addressType)
      );

      if (uspsAddress) {
        return { isValid: true, uspsAddress };
      }

      setIsUspsAddressError(true);
      return { isValid: false, uspsAddress: null };
    } catch (error) {
      setIsUspsAddressError(true);
      return { isValid: false, uspsAddress: null };
    }
  }

  async function checkZipUnderServiceArea(zip: string) {
    try {
      if (shouldValidateZip) {
        const serviceAreas = await getWandaServiceArea(zip);
        if (!serviceAreas.length) {
          setIsInvalidAddressModalVisible(true);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  function handleCantFindAddress() {
    setIsDisabled(false);
    setIsEnterManualAddress(true);
  }

  function handleResetAddressLookup() {
    if (form.getFieldValue("isSameAsHomeAddress")) {
      form.setFieldsValue({
        homeAddress: defaultSelectedAddress,
        mailingAddress: defaultSelectedAddress,
      });
    } else {
      form.setFieldsValue({
        [addressType]: defaultSelectedAddress,
      });
    }
    setIsUspsAddressError(false);
    setSelectedAddress(defaultSelectedAddress);
    setIsEnterManualAddress(false);
    setIsDisabled(true);
    setAutoCompleteOptions([]);
    validateAndCheckForm();
  }

  function handleDoesntLookQuiteRight() {
    setIsDisabled(false);
    setIsEnterManualAddress(true);
  }

  async function handleAddressSearch(searchText: string) {
    try {
      const data = await getAutoCompleteAddress(searchText);
      if (data && Array.isArray(data.predictions)) {
        const descriptions = data.predictions.map(
          (item: { description: string; place_id: string }) => ({
            value: item.place_id,
            label: item.description,
          })
        );
        setAutoCompleteOptions(descriptions);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to get auto complete address.",
      });
    }
  }

  async function handleAddressSelect(
    value: string,
    option: OptionData | OptionGroupData,
  ) {
    try {
      let tempAddressDetails: Address = { ...defaultSelectedAddress };
      const description =
        typeof option.label === "string" ? option.label : undefined;
      const placeDetails = await getPlaceDetails(
        option.value,
        description,
      );
      if (placeDetails.results) {
        const addressComponents = placeDetails.results[0].address_components;

        const { addressDetails } = constructAddressByPlaceDetails(
          tempAddressDetails,
          addressComponents,
        );

        tempAddressDetails = addressDetails;
      }

      setSelectedAddress(tempAddressDetails);

      form.setFieldsValue({
        [addressType]: tempAddressDetails,
      });

      if (form.getFieldValue("isSameAsHomeAddress")) {
        form.setFieldsValue({
          mailingAddress: tempAddressDetails,
        });
      }

      await checkZipUnderServiceArea(tempAddressDetails.zip);
    } catch (error) {
      notification.error({
        message: "Select Address Failed.",
      });
    } finally {
      validateAndCheckForm();
    }
  }

  async function handleAddressChange(fieldName: string) {
    try {
      isUspsAddressError && setIsUspsAddressError(false);

      if (form.getFieldValue("isSameAsHomeAddress")) {
        const address = form.getFieldValue("homeAddress");
        if (validateAddressObject(address)) {
          form.setFieldsValue({
            mailingAddress: address,
          });
          validateAndCheckForm();
        }
      }

      if (fieldName === "zip") {
        const { zip } = form.getFieldValue(addressType);

        if (zip?.length === 5) {
          await checkZipUnderServiceArea(zip);
        }
      }
    } catch (error) {
      notification.error({
        message: "Failed Address Check",
      });
    }
  }

  function handleAutoCompleteChange() {
    const fullAddress = form.getFieldValue(addressType)?.fullAddress;
    if (!fullAddress) {
      form.setFieldsValue({
        [addressType]: defaultSelectedAddress,
      });
      validateAndCheckForm();
    }
  }

  return (
    <>
      {!isEnterManualAddress && !selectedAddress.address1 ? (
        <>
          <Form.Item
            name={[addressType, "fullAddress"]}
            className={styles.formItemMargin}
          >
            <AutoComplete
              options={autoCompleteOptions}
              onSearch={handleAddressSearch}
              onSelect={handleAddressSelect}
              onChange={handleAutoCompleteChange}
              placeholder="Enter an address"
              className={styles.autoCompleteInput}
            />
          </Form.Item>
          <Row gutter={[0, 25]}>
            <CustomButton
              text={labels.cantFindAddressBtnTxt}
              type="link"
              handlerFunction={handleCantFindAddress}
              className="hyperLinkBtn"
            />
            <Col span={24} />
          </Row>
        </>
      ) : (
        <>
          <Form.Item
            name={[addressType, "address1"]}
            className={`${(!isEnterManualAddress || selectedAddress.address1) &&
              styles.formItemMargin}`}
          >
            <Input
              placeholder="Street Address"
              value={selectedAddress.address1}
              className={`${styles.input} ${isDisabled &&
                styles.disabledInput} ${isUspsAddressError &&
                styles.errorInputBorder}`}
              disabled={isDisabled}
              onChange={() => handleAddressChange("address1")}
            />
          </Form.Item>
          {selectedAddress.address1 && (
            <CustomButton
              text="Doesn't look quite right?"
              type="link"
              handlerFunction={handleDoesntLookQuiteRight}
              className="hyperLinkBtn"
            />
          )}

          <Form.Item name={[addressType, "address2"]}>
            <Input
              placeholder="Suite #, Apt #, PO Box, etc"
              className={`${styles.input} ${isUspsAddressError &&
                styles.errorInputBorder}`}
              onChange={() => handleAddressChange("address2")}
            />
          </Form.Item>

          <Row gutter={[10, 0]}>
            <Col>
              <Form.Item
                name={[addressType, "city"]}
                className={styles.formItemMargin}
              >
                <Input
                  placeholder="City"
                  className={`${styles.cityInput} ${isDisabled &&
                    styles.disabledInput} ${isUspsAddressError &&
                    styles.errorInputBorder}`}
                  value={selectedAddress.city}
                  disabled={isDisabled}
                  onChange={() => handleAddressChange("city")}
                />
              </Form.Item>
            </Col>

            <Col>
              <Form.Item
                name={[addressType, "state"]}
                className={styles.formItemMargin}
              >
                <Select
                  className={`${styles.stateSelect} ${isDisabled &&
                    styles.disabledSelectInput} ${isUspsAddressError &&
                    styles.errorSelectBorder}`}
                  placeholder="State"
                  options={states}
                  disabled={isDisabled}
                  suffixIcon=""
                  onChange={() => handleAddressChange("state")}
                />
              </Form.Item>
            </Col>

            <Col>
              <Form.Item
                name={[addressType, "zip"]}
                className={styles.formItemMargin}
                rules={[
                  {
                    min: 5,
                    message: "",
                  },
                ]}
              >
                <Input
                  maxLength={5}
                  placeholder="Zip Code"
                  className={`${styles.zipInput} ${isDisabled &&
                    styles.disabledInput} ${isUspsAddressError &&
                    styles.errorInputBorder}`}
                  value={selectedAddress.zip}
                  disabled={isDisabled}
                  onChange={() => handleAddressChange("zip")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Col
            span={24}
            className={`${!isUspsAddressError && styles.resetAddressBtnMargin}`}
          >
            <CustomButton
              text="Reset Address Lookup"
              type="link"
              handlerFunction={handleResetAddressLookup}
              className="hyperLinkBtn"
            />
          </Col>

          {isUspsAddressError && (
            <Col span={24} className={styles.invalidAddress}>
              Invalid Address
            </Col>
          )}
        </>
      )}

      <InvalidAddressModal
        form={form}
        addressType={addressType}
        visible={isInvalidAddressModalVisible}
        setVisibility={setIsInvalidAddressModalVisible}
        handleResetAddressLookup={handleResetAddressLookup}
      />
    </>
  );
});

export default PersonAddress;
